import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import SupplierFilter from "./SupplierFilter";
import ConfigurationSupplierModal from "./SupplierConfigurationModal";
import BankDetailsModal from "./BankDetailsModal";
import { Link } from "react-router-dom";
import { exportSuppliers, updateAggregatorStatus } from "../../../services/supplierService";
import { clearFilteredChips, getAdminSuppliersApi, setPage, resetAdminSupplierState, setFilteredChips } from "../../../redux/features/adminSupplierSlice";
import { errorToast, successToast } from "../../Toasts/Toasts";
import InfiniteScroll from "react-infinite-scroll-component";
import { getMasterData } from "../../../services/userService";
import { chipName, formatCreditBalance } from "../../../utils/sharedFunctions";
import { userRoleConstants } from "../../../utils/constants";
import localStorageStore from "../../../utils/localStorageStore";

const ManageSuppliers = () => {
  const dispatch = useDispatch();
  const { adminSupplierDetails: suppliers, isLoading: loading, error, filteredChips, totals, page, size, hasMore } = useSelector((state) => state.adminSupplier);
  const isAccountant = parseInt(localStorageStore.getRole()) === userRoleConstants.ACCOUNTANT_ID;

  const [showFilter, setShowFilter] = useState(false);
  const [showConfiguration, setShowConfiguration] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [masterData, setMasterData] = useState("");
  const [id, setId] = useState();
  const [statusOverrides, setStatusOverrides] = useState({});
  const [statusLoading, setStatusLoading] = useState({});

  // Initial load
  useEffect(() => {
    dispatch(getAdminSuppliersApi({ filteredData: {}, page: 1, size: 10 }))
      .then((response) => {
        if (response?.meta?.requestStatus === "rejected") {
          errorToast(response?.error?.message || "Failed to load suppliers");
        }
      });
  }, []);

  useEffect(() => {
    fetchMasterData();
    // eslint-disable-next-line
  }, []);

  const fetchMasterData = async () => {
    try {
      const response = await getMasterData(id);
      setMasterData(response?.data);
    } catch (error) {
      console.error(error?.message);
    }
  };

  const loadMoreData = useCallback(async () => {
    if (loading || !hasMore) return;
    
    let filterData = Object.fromEntries(filteredChips ?? []);
    let transformedChips = {};
    
    for (let key in filterData) {
      if (typeof filterData[key] === "object" && "label" in filterData[key] && "value" in filterData[key]) {
        transformedChips[key] = filterData[key].value;
      } else {
        transformedChips[key] = filterData[key];
      }
    }
    
    const nextPage = page + 1;
    const response = await dispatch(
      getAdminSuppliersApi({ filteredData: transformedChips, page: nextPage, size: 10 })
    );
    
    if (response?.meta?.requestStatus === "fulfilled") {
      dispatch(setPage(nextPage));
    } else {
      errorToast("Error occurred while loading more data");
    }
  }, [loading, hasMore, page, dispatch, filteredChips]);
  
    useEffect(() => {
      const handleScroll = () => {
        const scrollContainer = document.getElementById("adminSupplierComponent");
        if (
          scrollContainer &&
          scrollContainer.scrollHeight - scrollContainer.scrollTop === scrollContainer.clientHeight
        ) {
          loadMoreData();
        }
      };
  
      const scrollContainer = document.getElementById("adminSupplierComponent");
      if (scrollContainer) {
        scrollContainer.addEventListener("scroll", handleScroll);
      }
  
      return () => {
        if (scrollContainer) {
          scrollContainer.removeEventListener("scroll", handleScroll);
        }
      };
      // eslint-disable-next-line
    }, [loadMoreData]);
  
    useEffect(() => {
      return () => {
        dispatch(clearFilteredChips());
        dispatch(resetAdminSupplierState());
      };
      // eslint-disable-next-line
    }, []);

  const handleFilter = () => {
    setShowFilter(true);
  };
  
  // Handle status toggle
  const handleStatusChange = async (supplier) => {
    const supplierId = supplier?.id;

    if (!supplierId || statusLoading[supplierId]) return;

    // Determine the current status from overrides or initial data
    const currentStatus = statusOverrides.hasOwnProperty(supplierId)
      ? statusOverrides[supplierId]
      : supplier.active;

    const newStatus = Number(currentStatus) === 1 ? 0 : 1;

    try {
      setStatusLoading((prev) => ({ ...prev, [supplierId]: true }));
      let body = { status: newStatus };
      const response = await updateAggregatorStatus(supplierId, body);
      const updatedStatus = response?.data?.active ?? newStatus;

      setStatusOverrides((prev) => ({
        ...prev,
        [supplierId]: updatedStatus,
      }));

      successToast(response?.message || "Supplier status updated successfully");
    } catch (error) {
      errorToast(error?.response?.data?.message || "Failed to update supplier status");
    } finally {
      setStatusLoading((prev) => ({ ...prev, [supplierId]: false }));
    }
  };

  const handleExport = async () => {
    try {
      const filterData = Object.fromEntries(filteredChips ?? []);
      const transformedChips = {};
      for (let key in filterData) {
        if (typeof filterData[key] === "object" && "label" in filterData[key] && "value" in filterData[key]) {
          transformedChips[key] = filterData[key].value;
        } else {
          transformedChips[key] = filterData[key];
        }
      }
      const response = await exportSuppliers(transformedChips);
      const blob = new Blob([response], { type: "application/vnd.ms-excel; charset=utf-8" });
      const url = URL.createObjectURL(blob);
      let alink = document.createElement("a");
      alink.href = url;
      alink.download = "Suppliers_Report.xlsx";
      alink.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      const errorMessage = error?.response?.data?.message?.trim() || "Error downloading file";
      errorToast(errorMessage);
    }
  };

  // Handle settings click (open configuration modal)
  const handleSettingsClick = (supplier) => {
    setSelectedSupplier(supplier);
    setShowConfiguration(true);
  };

  const handleConfigurationSaved = () => {
    dispatch(resetAdminSupplierState());
    dispatch(getAdminSuppliersApi({ filteredData: {}, page: 1, size: 10 }));
  };

  const handleChipRemove = (key) => {
    const updatedChips = filteredChips.filter(([existingKey]) => existingKey !== key);
    dispatch(setFilteredChips(updatedChips));

    let filteredData = Object.fromEntries(updatedChips);
    let transformedChips = {};
    for (let k in filteredData) {
      if (typeof filteredData[k] === "object" && "label" in filteredData[k] && "value" in filteredData[k]) {
        transformedChips[k] = filteredData[k].value;
      } else {
        transformedChips[k] = filteredData[k];
      }
    }
    dispatch(getAdminSuppliersApi({ filteredData: transformedChips, page, size }));
  };


  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h4 className="pt-1 mb-1">Suppliers</h4>
      </div>

      <div className="row g-2">
        <div className="col-lg-2 col-12 mb-2">
          <div className="card">
            <div className="card-body p-2">
              <div className="d-flex align-items-center">
                <div className="badge rounded-pill bg-label-primary me-3 p-2">
                  <i className="ti ti-users ti-sm"></i>
                </div>
                <div className="card-info">
                  <h5 className="mb-0">{suppliers.length}</h5>
                  <small>All Suppliers</small>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-12 mb-2">
          <div className="card">
            <div className="card-body p-2">
              <div className="d-flex align-items-center">
                <div className="badge rounded-pill bg-label-info me-3 p-2">
                  <i className="ti ti-cash ti-sm"></i>
                </div>
                <div className="card-info">
                  <h5 className="mb-0">
                    {formatCreditBalance(totals?.totalPayments, 2)} <small>USD</small>
                  </h5>
                  <small>Total Payments</small>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-12 mb-2">
          <div className="card">
            <div className="card-body p-2">
              <div className="d-flex align-items-center">
                <div className="badge rounded-pill bg-label-warning me-3 p-2">
                  <i className="ti ti-cash ti-sm"></i>
                </div>
                <div className="card-info">
                  <h5 className="mb-0">
                    {formatCreditBalance(totals?.totalConsumed, 2)} <small>USD</small>
                  </h5>
                  <small>Total Consumed</small>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-12 mb-2">
          <div className="card">
            <div className="card-body p-2">
              <div className="d-flex align-items-center">
                <div className="badge rounded-pill bg-label-success me-3 p-2">
                  <i className="ti ti-cash ti-sm"></i>
                </div>
                <div className="card-info">
                  <h5 className="mb-0">
                    {formatCreditBalance(totals?.totalBalance, 2)} <small>USD</small>
                  </h5>
                  <small>Total Balance</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header pb-1 pt-1 border-bottom d-flex justify-content-between">
          <div className="w-100 d-flex align-items-center justify-content-between">
            <h5 className="card-title mb-0">Details</h5>

            <div className="d-flex align-items-center ms-auto">
              <div className="filter-card" style={{ display: "block" }}>
                {filteredChips?.map(
                  ([key, value]) =>
                    key !== "user_id" && (
                      <div className="chip" key={key}>
                        <input type="checkbox" name="filters[]" defaultChecked readOnly />
                        <span>
                          {chipName(key)}:{" "}
                          {typeof value === "object" && value?.label ? value.label : value}
                        </span>
                        <button type="button" className="zap">
                          <i className="ti ti-x fs-6" onClick={() => handleChipRemove(key)} />
                        </button>
                      </div>
                    )
                )}
              </div>

              <button type="submit" className="btn btn-sm btn-primary btn-sm waves-effect waves-light" onClick={handleFilter}>
                <i className="ti ti-filter"></i>
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill btn-sm waves-effect waves-light ms-2" onClick={handleExport}>
                <i className="ti ti-download me-1"></i>
                Export
              </button>
            </div>
          </div>
        </div>
        <InfiniteScroll
          dataLength={suppliers?.length ?? 0}
          next={loadMoreData}
          hasMore={hasMore}
          loader={null}
          scrollableTarget="adminSupplierComponent"
        >
          <div
            id="adminSupplierComponent"
            className="card-datatable table-responsive"
            style={{ height: "400px", overflowY: "auto" }}
          >
          <table className="table" id="suppliers">
            <thead className="fixed-table-header">
              <tr>
                <th>Name</th>
                <th>Commission</th>
                <th>Payments (USD)</th>
                <th>Payments (Local)</th>
                <th>Consumed (USD)</th>
                <th>Consumed (Local)</th>
                <th>Balance (USD)</th>
                <th>Balance (Local)</th>
                <th>Threshold (USD)</th>
                <th>Threshold (Local)</th>
                {!isAccountant && <th>Status</th>}
                {!isAccountant && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={isAccountant ? 10 : 12} className="text-center">
                    Loading suppliers...
                  </td>
                </tr>
              ) : suppliers.length === 0 ? (
                <tr>
                  <td colSpan={isAccountant ? 10 : 12} className="text-center">
                    No suppliers found
                  </td>
                </tr>
              ) : (
                suppliers.map((supplier, idx) => (
                <tr key={supplier?.id}>
                  <td>
                    <div>
                      <Link to={`/supplier-details/${supplier?.id}`}>{supplier?.name}</Link>
                    </div>
                    <small className="text-muted">ID: {supplier?.id}</small>
                  </td>
                  <td>
                    {supplier?.commission} <br />
                    <small className="text-muted">{supplier?.country}</small>
                  </td>
                  <td className="text-end">
                    {formatCreditBalance(supplier?.paymentsUSD, 2)} <br />
                    <small className="text-muted">USD</small>
                  </td>
                  <td className="text-end">
                    {formatCreditBalance(supplier?.paymentsLocalAmount, 2)} <br />
                    <small className="text-muted">{supplier?.paymentsLocalCurrency}</small>
                  </td>
                  <td className="text-end">
                    {formatCreditBalance(supplier?.consumedUSD, 2)} <br />
                    <small className="text-muted">USD</small>
                  </td>
                  <td className="text-end">
                    {formatCreditBalance(supplier?.consumedLocalAmount, 2)} <br />
                    <small className="text-muted">{supplier?.consumedLocalCurrency}</small>
                  </td>
                  <td className={`text-end ${supplier?.balanceStatus}`}>
                    {formatCreditBalance(supplier?.balanceUSD, 2)} <br />
                    <small className="text-muted">USD</small>
                  </td>
                  <td className="text-end">
                    {formatCreditBalance(supplier?.balanceLocalAmount, 2)} <br />
                    <small className="text-muted">{supplier?.balanceLocalCurrency}</small>
                  </td>
                  <td className="text-end">
                    {formatCreditBalance(supplier?.thresholdUSD, 2)} <br />
                    <small className="text-muted">USD</small>
                  </td>
                  <td className="text-end">
                    {formatCreditBalance(supplier?.thresholdLocalAmount, 2)} <br />
                    <small className="text-muted">{supplier?.thresholdLocalCurrency}</small>
                  </td>
                  {!isAccountant && (
                    <td>
                      <div className="d-flex align-items-center">
                        <input
                          className="input-switch"
                          type="checkbox"
                          id={`status${idx}`}
                          checked={
                            statusOverrides.hasOwnProperty(supplier?.id)
                              ? Number(statusOverrides[supplier?.id]) === 1
                              : Number(supplier?.active) === 1
                          }
                          disabled={statusLoading[supplier?.id]}
                          onChange={() => handleStatusChange(supplier)}
                        />
                        <label className="label-switch" htmlFor={`status${idx}`}></label>
                        <span className="info-text ms-2 "></span>
                      </div>
                    </td>
                  )}
                  {!isAccountant && (
                    <td>
                      <div className="btn-group" role="group" aria-label="First group">
                        <button type="button" className="btn btn-sm btn-outline-actions waves-effect" onClick={() => handleSettingsClick(supplier)}>
                          <i className="ti ti-settings text-blue"></i>
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              )))}

            </tbody>
          </table>
        </div>
        </InfiniteScroll>
      </div>

      {showFilter && <SupplierFilter showFilter={showFilter} setShowFilter={setShowFilter} masterData={masterData}/>}
      {showConfiguration && <ConfigurationSupplierModal
        supplierData={selectedSupplier}
        showConfiguration={showConfiguration}
        setShowConfiguration={setShowConfiguration}
        type={"Configuration"}
        masterData={masterData}
        setShowBankDetails={setShowBankDetails}
        onSaveSuccess={handleConfigurationSaved}
      />}
      {/* {showBankDetails && <BankDetailsModal show={showBankDetails} onHide={() => setShowBankDetails(false)} type={"Configuration"} />} */}
    </>
  );
};

export default ManageSuppliers;
